/* eslint-disable react-hooks/set-state-in-effect -- known false positive (react#34905) on async event handlers; not enforced by the app's build toolchain. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Globe, Check, X } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { Switch } from "@/components/jk/kit";
import { PageHeader, PageLoader, AdminCard, Field, Input, Btn, FilterChips } from "@/admin/components";

const roundInr = (x) => Math.floor(x + 0.5);

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [fee, setFee] = useState("");
  const [tcs, setTcs] = useState("");
  const [bookingWindow, setBookingWindow] = useState("30");
  const [subPrice, setSubPrice] = useState("");
  const [subMode, setSubMode] = useState("manual");
  const [mcEnabled, setMcEnabled] = useState(false);
  const [markup, setMarkup] = useState("7");
  const [currencies, setCurrencies] = useState(["GBP", "USD", "EUR"]);

  const toggleCurrency = (c) =>
    setCurrencies((arr) => (arr.includes(c) ? arr.filter((x) => x !== c) : [...arr, c]));

  const load = async () => {
    try {
      const { data } = await adminApi.get("/admin/settings");
      setCfg(data);
      setFee(String(data.platform_fee_percent));
      setTcs(String(data.tcs_percent));
      setBookingWindow(String(data.booking_window_days));
      setSubPrice(String(data.subscription_price_inr ?? 750));
      setSubMode(data.subscription_billing_mode || "manual");
      setMcEnabled(!!data.multicurrency_enabled);
      setMarkup(String(data.overseas_markup_percent ?? 7));
      setCurrencies(data.enabled_currencies || ["GBP", "USD", "EUR"]);
    } catch {
      toast.error("Couldn't load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    const feeN = parseFloat(fee);
    const tcsN = parseFloat(tcs);
    const priceN = parseInt(subPrice, 10);
    const markupN = parseFloat(markup);
    if (Number.isNaN(feeN) || feeN < 0 || feeN > 100) { toast.error("Platform fee must be 0–100%"); return; }
    if (Number.isNaN(tcsN) || tcsN < 0 || tcsN > 100) { toast.error("TCS must be 0–100%"); return; }
    if (Number.isNaN(priceN) || priceN < 1 || priceN > 100000) { toast.error("Subscription price must be ₹1–₹100000"); return; }
    if (mcEnabled && (Number.isNaN(markupN) || markupN < 0 || markupN > 100)) { toast.error("Overseas markup must be 0–100%"); return; }
    if (mcEnabled && currencies.length === 0) { toast.error("Select at least one currency, or turn off international payments"); return; }
    setSaving(true);
    try {
      const { data } = await adminApi.put("/admin/settings", {
        platform_fee_percent: feeN, tcs_percent: tcsN, booking_window_days: parseInt(bookingWindow, 10),
        subscription_price_inr: priceN, subscription_billing_mode: subMode,
        multicurrency_enabled: mcEnabled, enabled_currencies: currencies,
        overseas_markup_percent: Number.isNaN(markupN) ? 0 : markupN,
      });
      setCfg((c) => ({ ...c, ...data }));
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageLoader />;

  // Live preview on a sample ₹750 consultation
  const sample = 750;
  const feeN = parseFloat(fee) || 0;
  const tcsN = parseFloat(tcs) || 0;
  const platformFee = roundInr(sample * feeN / 100);
  const tcsAmt = roundInr(sample * tcsN / 100);
  const net = sample - platformFee - tcsAmt;

  return (
    <div data-testid="admin-settings-page">
      <PageHeader title="Settings" subtitle="Platform fees, taxes and booking rules" />

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        {/* Left column */}
        <div className="space-y-6">
        {/* Fees & tax */}
        <AdminCard className="p-6" testid="settings-fees-card">
          <h3 className="text-[15px] font-semibold text-[#111]">Platform fee & tax</h3>
          <p className="mt-1 text-[13px] text-[#777]">
            Model A — the platform fee is absorbed by the astrologer. Clients always pay the full
            consultation fee; the astrologer&apos;s payout is net of fee &amp; TCS.
          </p>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Field label="Platform fee %">
              <Input data-testid="settings-fee-input" type="number" min="0" max="100" step="0.1"
                value={fee} onChange={(e) => setFee(e.target.value)} />
            </Field>
            <Field label="TCS %">
              <Input data-testid="settings-tcs-input" type="number" min="0" max="100" step="0.1"
                value={tcs} onChange={(e) => setTcs(e.target.value)} />
            </Field>
          </div>

          <div className="mt-6">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#999]">Booking window</p>
            <FilterChips options={["30", "60"]} value={bookingWindow} onChange={setBookingWindow} testidPrefix="settings-window" />
            <p className="mt-1.5 text-[12px] text-[#999]">How many days ahead clients can book ({bookingWindow} days).</p>
          </div>

          <div className="mt-6">
            <Btn onClick={handleSave} busy={saving} testid="settings-save-btn">Save changes</Btn>
          </div>
        </AdminCard>

        {/* International payments (multi-currency) */}
        <AdminCard className="p-6" testid="settings-multicurrency-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-[#111]"><Globe size={16} /><h3 className="text-[15px] font-semibold">International payments</h3></div>
              <p className="mt-1 text-[13px] text-[#777]">Let overseas clients pay in GBP/USD/EUR (Stripe / UK Ltd). INR via Razorpay always works regardless of this switch.</p>
            </div>
            <Switch on={mcEnabled} onChange={setMcEnabled} testid="settings-multicurrency-toggle" />
          </div>

          <div>
            <div className="mt-5 max-w-[220px]">
              <Field label="Overseas markup %">
                <Input data-testid="settings-markup-input" type="number" min="0" max="100" step="0.5"
                  value={markup} onChange={(e) => setMarkup(e.target.value)} />
              </Field>
            </div>
            <div className="mt-4">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#999]">Accepted currencies</p>
              <div className="inline-flex rounded-[12px] border border-[#e5e5e5] bg-white p-1" data-testid="settings-currencies">
                {["GBP", "USD", "EUR"].map((c) => (
                  <button key={c} type="button" data-testid={`currency-${c}`} onClick={() => toggleCurrency(c)}
                    className={`rounded-[9px] px-3.5 py-2 text-[13px] font-medium transition-colors ${currencies.includes(c) ? "bg-[#111] text-white" : "text-[#666] hover:text-[#111]"}`}>{c}</button>
                ))}
              </div>
              <p className="mt-1.5 text-[12px] text-[#999]">Overseas price = INR list price × live FX × (1 + {markup || 0}% markup), rounded.</p>
            </div>
          </div>

          <div className="mt-6 rounded-[12px] border border-[#eee] bg-[#faf9f7] p-4" data-testid="settings-multicurrency-checklist">
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#999]">Before turning on (for production)</p>
            <div className="mt-3 space-y-2 text-[13px]">
              <CheckRow ok={cfg?.multicurrency_preconditions?.stripe_configured} label="Stripe (UK Ltd) keys configured" />
              <CheckRow ok={cfg?.multicurrency_preconditions?.fx_feed_healthy} label="Live FX feed reachable" />
              <CheckRow ok={cfg?.multicurrency_preconditions?.markup_set} label="Overseas markup set" />
            </div>
          </div>

          <div className="mt-6">
            <Btn onClick={handleSave} busy={saving} testid="settings-multicurrency-save-btn">Save changes</Btn>
          </div>
        </AdminCard>
        </div>

        {/* right column placeholder kept below */}
        <div className="space-y-6">
          <AdminCard className="p-6" testid="settings-subscription-card">
            <h3 className="text-[15px] font-semibold text-[#111]">Astrologer subscription</h3>
            <p className="mt-1 text-[13px] text-[#777]">Monthly fee each astrologer pays to keep accepting new clients beyond the free tier.</p>

            <div className="mt-5 max-w-[220px]">
              <Field label="Monthly price (₹)">
                <Input data-testid="settings-subprice-input" type="number" min="1" max="100000" step="1"
                  value={subPrice} onChange={(e) => setSubPrice(e.target.value)} />
              </Field>
              <p className="mt-1 text-[12px] text-[#999]">Applies to new sign-ups &amp; renewals. Existing AutoPay subscribers keep their approved amount until they re-authorise.</p>
            </div>

            <div className="mt-5">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#999]">Billing mode</p>
              <div className="inline-flex rounded-[12px] border border-[#e5e5e5] bg-white p-1" data-testid="settings-billing-mode">
                {[["manual", "Manual / pay-link"], ["razorpay_autopay", "Razorpay AutoPay"]].map(([val, label]) => (
                  <button key={val} type="button" data-testid={`billing-mode-${val}`}
                    onClick={() => setSubMode(val)}
                    className={`rounded-[9px] px-3.5 py-2 text-[13px] font-medium transition-colors ${subMode === val ? "bg-[#111] text-white" : "text-[#666] hover:text-[#111]"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[12px] leading-relaxed text-[#999]">
                {subMode === "manual"
                  ? "Astrologers pay ₹" + (subPrice || "750") + " online each month (one-time pay-link, 30-day access). You can also record offline payments per astrologer. Use this until Razorpay AutoPay is approved."
                  : "Astrologers approve a UPI AutoPay mandate once; Razorpay then auto-debits monthly. Switch here once your Razorpay AutoPay is live."}
              </p>
            </div>

            <div className="mt-6">
              <Btn onClick={handleSave} busy={saving} testid="settings-subscription-save-btn">Save changes</Btn>
            </div>
          </AdminCard>

          <AdminCard className="p-6" testid="settings-preview-card">
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#999]">Payout preview · ₹750 consultation</p>
            <div className="mt-4 space-y-2.5 text-[14px]">
              <Row label="Client pays (gross)" value={`₹${sample}`} />
              <Row label={`Platform fee (${feeN}%)`} value={`− ₹${platformFee}`} />
              <Row label={`TCS (${tcsN}%)`} value={`− ₹${tcsAmt}`} />
              <div className="border-t border-[#f0ede8] pt-2.5">
                <Row label="Astrologer net payout" value={`₹${net}`} bold testid="settings-preview-net" />
              </div>
            </div>
          </AdminCard>

          <AdminCard className="p-6" testid="settings-company-card">
            <div className="flex items-center gap-2 text-[#111]">
              <Building2 size={16} />
              <p className="text-[13px] font-semibold">Receipt entity</p>
            </div>
            <p className="mt-3 text-[14px] font-medium text-[#111]">{cfg?.company_name}</p>
            <p className="mt-1 text-[13px] text-[#777]">GSTIN {cfg?.company_gstin}</p>
            <p className="mt-3 text-[12px] leading-relaxed text-[#999]">Shown on every paid booking receipt.</p>
          </AdminCard>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, bold, testid }) => (
  <div className="flex items-center justify-between" data-testid={testid}>
    <span className={bold ? "font-semibold text-[#111]" : "text-[#666]"}>{label}</span>
    <span className={bold ? "text-[16px] font-bold text-[#111]" : "font-medium text-[#111]"}>{value}</span>
  </div>
);

const CheckRow = ({ ok, label }) => (
  <div className="flex items-center gap-2">
    {ok
      ? <Check size={15} className="shrink-0 text-[#1a8f4c]" strokeWidth={2.5} />
      : <X size={15} className="shrink-0 text-[#c0392b]" strokeWidth={2.5} />}
    <span className={ok ? "text-[#111]" : "text-[#777]"}>{label}</span>
  </div>
);
